import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NumberInput, Stack, Text } from "@mantine/core";


interface CommissionSplitInputProps {
  providerPercentage: number;
  onProviderPercentageChange: (value: number) => void;
  error?: string;
  disabled?: boolean;
}

export const CommissionSplitInput = ({
  providerPercentage,
  onProviderPercentageChange,
  error,
  disabled = false,
}: CommissionSplitInputProps) => {
  const { t } = useTranslation();

  // Auto-calculate platform percentage
  const platformPercentage = 100 - providerPercentage;

  // Validate that percentages are within bounds
  useEffect(() => {
    if (providerPercentage < 0 || providerPercentage > 100) {
      console.warn("Provider percentage out of bounds:", providerPercentage);
    }
  }, [providerPercentage]);

  return (
    <Stack gap="md">
      <Text size="sm" fw={500}>
        {t("default-commission-split")}
      </Text>

      {/* Provider Percentage Input */}
      <NumberInput
        label={t("provider-percentage")}
        placeholder="84.00"
        value={providerPercentage}
        onChange={(value) => {
          const numValue = typeof value === "number" ? value : 0;
          // Clamp value between 0 and 100
          const clampedValue = Math.max(0, Math.min(100, numValue));
          onProviderPercentageChange(clampedValue);
        }}
        min={0}
        max={100}
        step={0.01}
        decimalScale={2}
        fixedDecimalScale
        disabled={disabled}
        error={error}
        suffix=" %"
        styles={{
          input: {
            textAlign: "right",
          },
        }}
      />

      {/* Platform Percentage (Read-only, auto-calculated) */}
      <NumberInput
        label={t("platform-percentage")}
        placeholder="16.00"
        value={platformPercentage}
        disabled
        suffix=" %"
        description={t("auto-calculated")}
        styles={{
          input: {
            textAlign: "right",
            backgroundColor: "var(--clr-gray-100)",
          },
        }}
      />

      {/* Validation info */}
      {providerPercentage + platformPercentage !== 100 && (
        <Text size="xs" c="red">
          {t("percentages-must-total-100")}
        </Text>
      )}
    </Stack>
  );
};
